// Pages/TransactionList.jsx
import React, { useEffect, useState } from "react";
import { List, Datagrid, TextField, DateField } from "react-admin";
import { Card, CardContent, Grid } from "@mui/material";

import {
  Box,
  TextField as MuiTextField,
  Button,
  Autocomplete,
  CircularProgress,
  Typography,
} from "@mui/material";
import { fetchTransactions } from "../../Utilities/UserData";
import { Parse } from "parse";
import CustomPagination from "../../Components/CustomPagination";
Parse.initialize(
  process.env.REACT_APP_APP_ID,
  null,
  process.env.REACT_APP_MASTER_KEY
);
Parse.masterKey = process.env.REACT_APP_MASTER_KEY;
Parse.serverURL = process.env.REACT_APP_SERVER_URL;

const CustomFilter = ({ onFilter }) => {
  const today = new Date().toISOString().split("T")[0];
  const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0];

  const [startDate, setStartDate] = useState(oneWeekAgo);
  const [endDate, setEndDate] = useState(today);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSearch = async (inputValue) => {
    if (!inputValue) {
      setUserOptions([]);
      return;
    }

    setLoadingUsers(true);
    try {
      const query = new Parse.Query(Parse.User);
      query.limit(20); // Limit for performance
      query.select(["username", "role"]);
      query.include("role");

      query.matches("username", inputValue, "i"); // Case-insensitive search

      const results = await query.find();

      const users = results.map((u) => ({
        id: u.id,
        username: u.get("username"),
        role: u.get("role")?.get("name") || "Unknown",
      }));

      setUserOptions(users);
    } catch (err) {
      console.error("User search failed:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSubmit = () => {
    setSubmitted(true);
    if (!selectedUser) return;
    const users = [selectedUser];
    onFilter({ startDate, endDate, users });
  };
  return (
    <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mt={4}>
      <MuiTextField
        label="Start Date"
        type="date"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <MuiTextField
        label="End Date"
        type="date"
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <Autocomplete
        options={userOptions}
        getOptionLabel={(option) => `${option.username} (${option.role})`}
        value={selectedUser}
        onChange={(e, newVal) => setSelectedUser(newVal)}
        onInputChange={(_, value) => {
          setSearchText(value);
          handleSearch(value); // 🔍 Fetch users on input
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        loading={loadingUsers}
        renderInput={(params) => (
          <MuiTextField
            {...params}
            label="Search User"
            placeholder="Enter username..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingUsers ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            error={!selectedUser && submitted}
            helperText={!selectedUser && submitted ? "Username is required" : ""}
          />
        )}
        sx={{ minWidth: 300 }}
      />
      <Button variant="contained" onClick={handleSubmit} >
        Apply Filters
      </Button>
    </Box>
  );
};

export const TransactionList = () => {
  const [summary, setSummary] = useState(null); // null = hidden by default
  const [loading, setLoading] = useState(false);

  const handleFilterChange = async (filterValues) => {
    setLoading(true);
    try {
      const {
        totalRecharge,
        totalRedeem,
        totalBetAmount,
        totalWinAmount,
        rechargeRatio,
      } = await fetchTransactions(filterValues);

      setSummary({
        totalRecharge,
        totalRedeem,
        totalBetAmount,
        totalWinAmount,
        rechargeRatio,
      });
    } catch (err) {
      console.error("Failed to fetch transactions", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <CustomFilter onFilter={handleFilterChange} />

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
          <CircularProgress size={40} />
        </Box>
      )}

      {!loading && summary && (
        <Grid item xs={12} sm={12} md={12} sx={{ mt: 1 }}>
          <Card
            variant="outlined"
            sx={{
              mt: 2,
              backgroundColor: "#242424",
              borderRadius: "8px",
            }}
          >
            <CardContent>
              <Typography
                gutterBottom
                variant="h5"
                component="div"
                sx={{ fontSize: 22, fontWeight: 500, color: "#FFF" }}
              >
                User Summary
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4} md={4}>
                  <SummaryCard title="Total Recharge" value={summary.totalRecharge} />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <SummaryCard title="Total Redeem" value={summary.totalRedeem} />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <SummaryCard title="Total Bet Amount" value={summary.totalBetAmount} />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <SummaryCard title="Total Win Amount" value={summary.totalWinAmount} />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <SummaryCard title="Holding" value={summary.rechargeRatio} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Box>
  );
};

// Reusable summary card component
const SummaryCard = ({ title, value }) => (
  <Card
    variant="outlined"
    sx={{
      mt: 1,
      border: "1px solid #1A1A1A",
      background: "#1A1A1A",
    }}
  >
    <CardContent>
      <Typography
        variant="body2"
        sx={{
          fontSize: 14,
          fontWeight: 400,
          color: "rgba(255, 255, 255, 0.70)",
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontSize: 24,
          fontWeight: 400,
          color: "white",
          marginTop: 2,
        }}
      >
        {value}
      </Typography>
    </CardContent>
  </Card>
);

  